import React, { useState, useEffect } from 'react';
import {
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  LinearProgress,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Assignment as AssignmentIcon,
  Engineering as EngineeringIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import './CallTrackingForm.css';

const CallTrackingForm = ({
  callData,
  setCallData,
  inspections,
  setInspections,
  newInspection,
  setNewInspection,
  showInspectionForm,
  setShowInspectionForm,
  editingInspectionIndex,
  setEditingInspectionIndex,
  errors,
  setErrors,
  onSubmit,
  onReset,
  isSubmitting,
  isEditMode = false,
  configuredDepartments = [],
  configuredAssetTypes = [],
  createInspection,
  updateInspection,
  deleteInspection,
  editingCallId,
  user,
  onCallUpdated
}) => {
  const [inspectionLoading, setInspectionLoading] = useState(false);
  const [callUpdateLoading, setCallUpdateLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper function to format date for input
  const formatDateForInput = (date) => {
    return format(date, 'yyyy-MM-dd');
  };

  // Helper function to parse date from input
  const parseDateFromInput = (dateString) => {
    return parseISO(dateString);
  };

  // Dropdown options - use configured options if available, otherwise fallback to defaults
  // Handle both old format (strings) and new format (objects with emails)
  const departmentOptions = configuredDepartments.length > 0 
    ? (typeof configuredDepartments[0] === 'string' 
        ? configuredDepartments 
        : configuredDepartments.map(dept => dept.name))
    : [
        'Public Works',
        'Streets', 
        'Water',
        'Parks',
        'Fire Department'
      ];

  // Asset type options now include both configured types and previously used values from calls
  const assetTypeOptions = configuredAssetTypes.length > 0 ? configuredAssetTypes : [
    'Hydrant',
    'Sign',
    'Valve', 
    'Manhole',
    'Water Line',
    'Water Meter',
    'Sewer Line'
  ];

  const statusOptions = ['Open', 'Closed'];

  // Mock data for previous users (in real app, this would come from API)
  const previousUsers = [
    'John Smith',
    'Jane Doe', 
    'Mike Johnson',
    'Sarah Wilson',
    'Tom Brown'
  ];

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    
    if (!callData.callerInformation.trim()) {
      newErrors.callerInformation = 'Caller information is required';
    }
    if (!callData.responsibleDepartment) {
      newErrors.responsibleDepartment = 'Responsible department is required';
    }
    if (!callData.locationDescription.trim()) {
      newErrors.locationDescription = 'Location description is required';
    }
    if (!callData.issueDetails.trim()) {
      newErrors.issueDetails = 'Issue details are required';
    }

    // Email validation (optional but must be valid if provided)
    if (callData.callerEmail && callData.callerEmail.trim() && !validateEmail(callData.callerEmail.trim())) {
      newErrors.callerEmail = 'Please enter a valid email address';
    }

    // Phone validation (optional but must be valid if provided)
    if (callData.callerPhone && callData.callerPhone.trim()) {
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      if (!phoneRegex.test(callData.callerPhone.trim().replace(/[\s\-\(\)]/g, ''))) {
        newErrors.callerPhone = 'Please enter a valid phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form field changes
  const handleCallDataChange = (field, value) => {
    setCallData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle inspection field changes
  const handleInspectionChange = (field, value) => {
    setNewInspection(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add new inspection
  const addInspection = async () => {
    if (!newInspection.descriptionOfWork.trim()) {
      alert('Please fill in the description of work');
      return;
    }

    setInspectionLoading(true);
    
    try {
      const inspectionData = {
        ...newInspection,
        date: newInspection.date,
        overallCondition: newInspection.overallCondition || 'Good'
      };

        const result = await createInspection(inspectionData);
      
      if (result.success) {
        // Reset form
        setNewInspection({
          date: formatDateForInput(new Date()),
          descriptionOfWork: '',
          closeCall: false,
          overallCondition: 'Good'
        });

        // Close call if requested - update both local state and database
        if (newInspection.closeCall) {
          setCallData(prev => ({ ...prev, status: 'Closed' }));
          
          // Update the call record in the database
          setCallUpdateLoading(true);
          try {
            const apiData = {
              ClientID: user.ClientID,
              CallDetailID: editingCallId,
              dateReceived: callData.dateReceived,
              callerInformation: callData.callerInformation,
              responsibleDepartment: callData.responsibleDepartment,
              appliesTo: callData.appliesTo,
              locationDescription: callData.locationDescription,
              issueDetails: callData.issueDetails,
              status: 'Closed', // Set status to Closed
              Last_Editor: user.FirstName + ' ' + user.LastName,
              Last_Edited_Date: new Date().toISOString()
            };

            const response = await fetch('/api/callUpdate', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(apiData)
            });

            if (!response.ok) {
              console.error('Failed to update call status to Closed');
            } else {
              // Call the callback to refresh the main calls page
              if (onCallUpdated) {
                onCallUpdated();
              }
            }
          } catch (error) {
            console.error('Error updating call status:', error);
          } finally {
            setCallUpdateLoading(false);
          }
        }

        setShowInspectionForm(false);
      } else {
        alert('Failed to create inspection: ' + result.error);
      }
    } catch (error) {
      console.error('Error creating inspection:', error);
      alert('Error creating inspection. Please try again.');
    } finally {
      setInspectionLoading(false);
    }
  };

  // Edit inspection
  const editInspection = (index) => {
    setNewInspection(inspections[index]);
    setEditingInspectionIndex(index);
    setShowInspectionForm(true);
  };

  // Update inspection
  const updateInspectionHandler = async () => {
    if (!newInspection.descriptionOfWork.trim()) {
      alert('Please fill in the description of work');
      return;
    }

    setInspectionLoading(true);
    
    try {
      const inspectionData = {
        ...newInspection,
        InspectionID: inspections[editingInspectionIndex].InspectionID,
        date: newInspection.date,
        overallCondition: newInspection.overallCondition || 'Good'
      };

      const result = await updateInspection(inspectionData);
      
      if (result.success) {
        // Close call if requested - update both local state and database
        if (newInspection.closeCall) {
          setCallData(prev => ({ ...prev, status: 'Closed' }));
          
          // Update the call record in the database
          setCallUpdateLoading(true);
          try {
            const apiData = {
              ClientID: user.ClientID,
              CallDetailID: editingCallId,
              dateReceived: callData.dateReceived,
              callerInformation: callData.callerInformation,
              responsibleDepartment: callData.responsibleDepartment,
              appliesTo: callData.appliesTo,
              locationDescription: callData.locationDescription,
              issueDetails: callData.issueDetails,
              status: 'Closed', // Set status to Closed
              Last_Editor: user.FirstName + ' ' + user.LastName,
              Last_Edited_Date: new Date().toISOString()
            };

            const response = await fetch('/api/callUpdate', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(apiData)
            });

            if (!response.ok) {
              console.error('Failed to update call status to Closed');
            } else {
              // Call the callback to refresh the main calls page
              if (onCallUpdated) {
                onCallUpdated();
              }
            }
          } catch (error) {
            console.error('Error updating call status:', error);
          } finally {
            setCallUpdateLoading(false);
          }
        }

        // Reset form
        setNewInspection({
          date: formatDateForInput(new Date()),
          descriptionOfWork: '',
          closeCall: false,
          overallCondition: 'Good'
        });

        setEditingInspectionIndex(null);
        setShowInspectionForm(false);
      } else {
        alert('Failed to update resolution: ' + result.error);
      }
    } catch (error) {
      console.error('Error updating resolution:', error);
      alert('Error updating resolution. Please try again.');
    } finally {
      setInspectionLoading(false);
    }
  };

  // Delete inspection
  const deleteInspectionHandler = async (index) => {
    const inspectionID = inspections[index].InspectionID;
    const result = await deleteInspection(inspectionID);
    
    if (!result.success) {
      alert('Failed to delete resolution: ' + result.error);
    }
  };

  return (
    <Box 
      className="call-tracking-form"
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[50]} 100%)`,
        minHeight: '100vh',
        py: 3,
        px: isMobile ? 2 : 4
      }}
    >
      {(isSubmitting || callUpdateLoading) && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            {isSubmitting ? 'Submitting call data...' : 'Updating call status...'}
          </Typography>
        </Box>
      )}

      <Stack spacing={3} maxWidth="1200px" mx="auto">
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            {isEditMode ? 'Edit Call Record' : 'New Call Tracking'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage service calls efficiently
          </Typography>
        </Box>

        {/* Call Information Card */}
        <Card 
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
              boxShadow: theme.shadows[8],
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease-in-out'
            }
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              p: 2
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <PhoneIcon />
              </Avatar>
              <Typography variant="h6" fontWeight={600}>
                Call Information
              </Typography>
            </Box>
          </Box>
          
          <CardContent sx={{ padding: isMobile ? 3 : 4 }}>
            <Box display="flex" flexDirection="column" gap={3}>
              <TextField
                fullWidth
                label="Date Received"
                type="date"
                value={callData.dateReceived}
                onChange={(e) => handleCallDataChange('dateReceived', e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!errors.dateReceived}
                helperText={errors.dateReceived}
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  }
                }}
              />
              
              <TextField
                fullWidth
                label="Caller Name"
                value={callData.callerInformation}
                onChange={(e) => handleCallDataChange('callerInformation', e.target.value)}
                error={!!errors.callerInformation}
                helperText={errors.callerInformation || "Enter the caller's name"}
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  }
                }}
              />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address (Optional)"
                    type="email"
                    value={callData.callerEmail || ''}
                    onChange={(e) => handleCallDataChange('callerEmail', e.target.value)}
                    error={!!errors.callerEmail}
                    helperText={errors.callerEmail || "Enter email for notifications"}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: isMobile ? <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} /> : null
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number (Optional)"
                    type="tel"
                    value={callData.callerPhone || ''}
                    onChange={(e) => handleCallDataChange('callerPhone', e.target.value)}
                    error={!!errors.callerPhone}
                    helperText={errors.callerPhone || "Enter phone number for follow-up"}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: isMobile ? <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} /> : null
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ minWidth: { md: 250 } }}>
                  <TextField
                    fullWidth
                    select
                    label="Responsible Department"
                    value={callData.responsibleDepartment}
                    onChange={(e) => handleCallDataChange('responsibleDepartment', e.target.value)}
                    error={!!errors.responsibleDepartment}
                    helperText={errors.responsibleDepartment}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: isMobile ? <AssignmentIcon sx={{ mr: 1, color: 'text.secondary' }} /> : null
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Department</em>
                    </MenuItem>
                    {departmentOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} sx={{ minWidth: { md: 250 } }}>
                  <Autocomplete
                    fullWidth
                    freeSolo
                    options={assetTypeOptions}
                    value={callData.appliesTo || ''}
                    onChange={(event, newValue) => {
                      handleCallDataChange('appliesTo', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      handleCallDataChange('appliesTo', newInputValue || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Asset Type"
                        error={!!errors.appliesTo}
                        helperText={errors.appliesTo}
                        size={isMobile ? "small" : "medium"}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: isMobile ? (
                            <>
                              <EngineeringIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              {params.InputProps.startAdornment}
                            </>
                          ) : params.InputProps.startAdornment
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                            },
                          }
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
              </Grid>
              
              {/* Contact Information Summary */}
              {(callData.callerEmail || callData.callerPhone) && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Contact Information Collected:</strong>
                  </Typography>
                  {callData.callerEmail && (
                    <Typography variant="body2" component="div">
                      📧 Email: {callData.callerEmail}
                    </Typography>
                  )}
                  {callData.callerPhone && (
                    <Typography variant="body2" component="div">
                      📞 Phone: {callData.callerPhone}
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary">
                    Notifications will be sent to the provided contact information.
                  </Typography>
                </Alert>
              )}
              
              <TextField
                fullWidth
                label="Location Description"
                value={callData.locationDescription}
                onChange={(e) => handleCallDataChange('locationDescription', e.target.value)}
                multiline
                rows={isMobile ? 2 : 3}
                error={!!errors.locationDescription}
                helperText={errors.locationDescription}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: isMobile ? <LocationIcon sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 1 }} /> : null
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  }
                }}
              />
              
              <TextField
                fullWidth
                label="Issue Details"
                value={callData.issueDetails}
                onChange={(e) => handleCallDataChange('issueDetails', e.target.value)}
                multiline
                rows={isMobile ? 3 : 4}
                error={!!errors.issueDetails}
                helperText={errors.issueDetails}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: isMobile ? <DescriptionIcon sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 1 }} /> : null
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  }
                }}
              />
              
              <FormControl component="fieldset" fullWidth>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Status
                </Typography>
                <RadioGroup
                  row={!isSmallMobile}
                  value={callData.status}
                  onChange={(e) => handleCallDataChange('status', e.target.value)}
                  sx={{
                    '& .MuiFormControlLabel-root': {
                      mr: 3,
                      '& .MuiRadio-root': {
                        color: theme.palette.primary.main,
                      }
                    }
                  }}
                >
                  {statusOptions.map((option) => (
                    <FormControlLabel
                      key={option}
                      value={option}
                      control={<Radio size={isMobile ? "small" : "medium"} />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          {option === 'Open' ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          ) : (
                            <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
                          )}
                          {option}
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {/* Inspections Card */}
        <Card 
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
              boxShadow: theme.shadows[8],
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease-in-out'
            }
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
              color: 'white',
              p: 2
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <EngineeringIcon />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  Resolutions
                </Typography>
                {inspections.length > 0 && (
                  <Chip 
                    label={`${inspections.length} inspection${inspections.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                )}
              </Box>
              <Button
                size="small"
                startIcon={showInspectionForm ? <ExpandLess /> : <AddIcon />}
                onClick={() => setShowInspectionForm(!showInspectionForm)}
                variant="outlined"
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
              </Button>
            </Box>
          </Box>
          
          <CardContent sx={{ padding: isMobile ? 3 : 4 }}>
            <Collapse in={showInspectionForm}>
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  borderRadius: 2,
                  background: theme.palette.grey[50],
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight={600} color="primary">
                  {editingInspectionIndex !== null ? 'Edit Resolution' : 'New Resolution'}
                </Typography>
                
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    value={newInspection.date}
                    onChange={(e) => handleInspectionChange('date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                  

                  
                  <TextField
                    fullWidth
                    label="Description of Work"
                    value={newInspection.descriptionOfWork}
                    onChange={(e) => handleInspectionChange('descriptionOfWork', e.target.value)}
                    multiline
                    rows={isMobile ? 2 : 3}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    select
                    label="Overall Condition"
                    value={newInspection.overallCondition}
                    onChange={(e) => handleInspectionChange('overallCondition', e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  >
                    <MenuItem value="Good">Good</MenuItem>
                    <MenuItem value="Fair">Fair</MenuItem>
                    <MenuItem value="Poor">Poor</MenuItem>
                    <MenuItem value="Needs Replacement">Needs Replacement</MenuItem>
                  </TextField>
                  
                  <Box display="flex" gap={2} alignItems="center" flexDirection={isSmallMobile ? "column" : "row"}>
                    <FormControlLabel
                      control={
                        <input
                          type="checkbox"
                          checked={newInspection.closeCall}
                          style={{ width: '100px' }}
                          onChange={(e) => handleInspectionChange('closeCall', e.target.checked)}
                        />
                      }
                      label="Close Call After This Resolution"
                      sx={{ flex: 1 }}
                    />
                    
                    {editingInspectionIndex !== null ? (
                      <>
                        <Button
                          variant="contained"
                          size="medium"
                          onClick={updateInspectionHandler}
                          disabled={inspectionLoading || callUpdateLoading}
                          startIcon={inspectionLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                          sx={{ borderRadius: 2, minWidth: 140 }}
                        >
                          {inspectionLoading ? 'Updating...' : 'Update Resolution'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="medium"
                          disabled={inspectionLoading || callUpdateLoading}
                          onClick={() => {
                            setEditingInspectionIndex(null);
                            setShowInspectionForm(false);
                            setNewInspection({
                              date: formatDateForInput(new Date()),
                              descriptionOfWork: '',
                              closeCall: false,
                              overallCondition: 'Good'
                            });
                          }}
                          startIcon={<CancelIcon />}
                          sx={{ borderRadius: 2, minWidth: 100 }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        size="medium"
                        onClick={addInspection}
                        disabled={inspectionLoading || callUpdateLoading}
                        sx={{ 
                          borderRadius: '50%',
                          minWidth: 48,
                          width: 48,
                          height: 48,
                          p: 0
                        }}
                      >
                        {inspectionLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Collapse>
            
            {inspections.length > 0 && (
              <TableContainer 
                component={Paper} 
                variant="outlined" 
                sx={{ 
                  maxHeight: isMobile ? 300 : 400,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Table size={isMobile ? "small" : "medium"} stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 100, fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 100, fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 100, fontWeight: 600 }}>Condition</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 100, fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inspections.map((inspection, index) => (
                      <TableRow 
                        key={inspection.id}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: theme.palette.action.hover 
                          }
                        }}
                      >
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          <Chip 
                            label={format(parseDateFromInput(inspection.date), 'MM/dd/yyyy')}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                              {inspection.loggedBy.charAt(0)}
                            </Avatar>
                            {inspection.loggedBy}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: isMobile ? '0.75rem' : '0.875rem',
                              maxWidth: isMobile ? 120 : 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {inspection.descriptionOfWork}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={inspection.overallCondition || 'Good'}
                            size="small"
                            color={
                              inspection.overallCondition === 'Good' ? 'success' :
                              inspection.overallCondition === 'Fair' ? 'warning' :
                              inspection.overallCondition === 'Poor' ? 'error' :
                              inspection.overallCondition === 'Needs Replacement' ? 'error' : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <IconButton 
                              size="small" 
                              onClick={() => editInspection(index)}
                              sx={{ 
                                padding: isMobile ? 0.5 : 1,
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  bgcolor: theme.palette.primary.light + '20'
                                }
                              }}
                            >
                              <EditIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => deleteInspectionHandler(index)}
                              sx={{ 
                                padding: isMobile ? 0.5 : 1,
                                color: theme.palette.error.main,
                                '&:hover': {
                                  bgcolor: theme.palette.error.light + '20'
                                }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>
      
      {/* Form Actions */}
      <Box 
        display="flex" 
        justifyContent="center" 
        gap={3} 
        mt={4}
        flexDirection={isSmallMobile ? "column" : "row"}
        maxWidth="1200px"
        mx="auto"
        px={isMobile ? 2 : 4}
      >
        <Button
          variant="outlined"
          onClick={onReset}
          disabled={isSubmitting}
          fullWidth={isSmallMobile}
          size="large"
          startIcon={<RefreshIcon />}
          sx={{ 
            borderRadius: 2,
            py: 1.5,
            px: 4,
            fontWeight: 600
          }}
        >
          Reset Form
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isSubmitting}
          fullWidth={isSmallMobile}
          size="large"
          startIcon={isEditMode ? <SaveIcon /> : <AddIcon />}
          sx={{ 
            borderRadius: 2,
            py: 1.5,
            px: 4,
            fontWeight: 600,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            }
          }}
        >
          {isSubmitting ? 'Submitting...' : (isEditMode ? 'Update Call' : 'Submit Call')}
        </Button>
      </Box>
    </Box>
  );
};

export default CallTrackingForm; 
